import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./PayrollSyncPage.css";

const DAY_START_HOUR = 5;
const DAY_END_HOUR = 23;
const TOTAL_HOURS = DAY_END_HOUR - DAY_START_HOUR;



const formatDisplayDate = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return value;
  return `${month}/${day}/${year}`;
};



const extractDatePart = (value) => {
  if (!value) return "";
  const str = String(value).trim();
  const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

const extractTimePart = (value) => {
  if (!value) return "";
  const str = String(value).trim();

  // Works for:
  // 2025-04-07 14:10:00
  // 2025-04-07T14:10:00.000Z
  // 14:10:00
  const match = str.match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return "";

  const hh = match[1];
  const mm = match[2];
  const ss = match[3] || "00";
  return `${hh}:${mm}:${ss}`;
};

const formatTimeLabel = (value) => {
  const timePart = extractTimePart(value);
  if (!timePart) return "";

  let [hour, minute] = timePart.split(":").map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return "";

  const ampm = hour >= 12 ? "pm" : "am";
  hour = hour % 12 || 12;

  return `${hour}:${String(minute).padStart(2, "0")}${ampm}`;
};

const getDecimalHour = (value) => {
  const timePart = extractTimePart(value);
  if (!timePart) return null;

  const [hour, minute] = timePart.split(":").map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;

  return hour + minute / 60;
};

const formatDate = (value) => {
  return extractDatePart(value);
};





const getDateRangeArray = (start, end) => {
  if (!start || !end) return [];

  const dates = [];
  let current = start;

  while (current <= end) {
    dates.push(current);

    const [year, month, day] = current.split("-").map(Number);
    const next = new Date(year, month - 1, day + 1);

    const nextYear = next.getFullYear();
    const nextMonth = String(next.getMonth() + 1).padStart(2, "0");
    const nextDay = String(next.getDate()).padStart(2, "0");

    current = `${nextYear}-${nextMonth}-${nextDay}`;
  }

  return dates;
};

const splitIntoWeeks = (dates) => {
  const weeks = [];
  for (let i = 0; i < dates.length; i += 7) {
    weeks.push(dates.slice(i, i + 7));
  }
  return weeks;
};

const coerceArray = (value) => (Array.isArray(value) ? value : []);

const PayrollSyncPage = () => {
  const scrollRef = React.useRef(null);
  const { id: businessId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [storeNumber, setStoreNumber] = useState("");
  const [startDate, setStartDate] = useState(formatDate(location.state?.startDate));
  const [endDate, setEndDate] = useState(formatDate(location.state?.endDate));

  const [payrollRows, setPayrollRows] = useState([]);
  const [tipRows, setTipRows] = useState([]);
  const [employeeMappings, setEmployeeMappings] = useState([]);

  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState("");
  const [mappingWarning, setMappingWarning] = useState("");
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [editClockIn, setEditClockIn] = useState("");
  const [editClockOut, setEditClockOut] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    const loadPayPeriod = async () => {
      if (startDate && endDate) {
        setLoadingDates(false);
        return;
      }

      try {
        setLoadingDates(true);
        setError("");

        const payPeriodRes = await api.get(`/payroll/next/${businessId}`);
        setStartDate(formatDate(payPeriodRes.data?.start_date));
        setEndDate(formatDate(payPeriodRes.data?.end_date));
      } catch (err) {
        console.error("Error fetching pay period:", err.response?.data || err.message);
        setError("Failed to load payroll dates.");
      } finally {
        setLoadingDates(false);
      }
    };

    loadPayPeriod();
  }, [businessId, startDate, endDate]);

  useEffect(() => {
    if (!startDate || !endDate) {
      setLoadingData(false);
      return;
    }

    const loadSubwayData = async () => {
      try {
        setLoadingData(true);
        setError("");
        setMappingWarning("");

        const storeRes = await api.get(`/subway-data/business-store/${businessId}`);
        const resolvedStoreNumber = String(storeRes.data?.store_number || "").trim();

        if (!resolvedStoreNumber) {
          setStoreNumber("");
          setPayrollRows([]);
          setTipRows([]);
          setEmployeeMappings([]);
          setError("No Subway store number found for this business.");
          return;
        }

        setStoreNumber(resolvedStoreNumber);

        const [payrollRes, tipsRes] = await Promise.all([
          api.get(`/subway-data/payroll-sync/${resolvedStoreNumber}`, {
            params: { start_date: startDate, end_date: endDate },
          }),
          api.get(`/subway-data/tips-sync/${resolvedStoreNumber}`, {
            params: { start_date: startDate, end_date: endDate },
          }),
        ]);

        setPayrollRows(coerceArray(payrollRes.data));
        setTipRows(coerceArray(tipsRes.data));

        // Mapping fetch is non-fatal so the page still works.
        try {
          let mappings = [];

          try {
            const mappingsRes = await api.get(`/subway-data/employee-mappings/${resolvedStoreNumber}`);
            mappings = coerceArray(mappingsRes.data);
          } catch (firstErr) {
            const fallbackRes = await api.get(`/subway-data/employee-mappings`, {
              params: { store_number: resolvedStoreNumber, business_id: businessId },
            });
            mappings = coerceArray(fallbackRes.data);
          }

          setEmployeeMappings(mappings);
        } catch (mappingErr) {
          console.error("Mapping endpoint failed:", mappingErr.response?.data || mappingErr.message);
          setEmployeeMappings([]);
          setMappingWarning("Employee mappings could not be loaded. The page still works, but Finalize will stay disabled until mappings load.");
        }
      } catch (err) {
        console.error("Error loading Subway payroll sync data:", err.response?.data || err.message);
        setPayrollRows([]);
        setTipRows([]);
        setEmployeeMappings([]);
        setError("Failed to load Subway payroll data.");
      } finally {
        setLoadingData(false);
      }
    };

    loadSubwayData();
  }, [businessId, startDate, endDate]);

  const allDates = useMemo(() => getDateRangeArray(startDate, endDate), [startDate, endDate]);
  const weeks = useMemo(() => splitIntoWeeks(allDates), [allDates]);
  const currentWeekDates = useMemo(() => weeks[currentWeekIndex] || [], [weeks, currentWeekIndex]);

  useEffect(() => {
    setCurrentWeekIndex(0);
  }, [startDate, endDate]);

  const mappingBySubwayId = useMemo(() => {
    const map = {};

    employeeMappings.forEach((row) => {
      const subwayId = String(
        row.subway_employee_id ??
          row.clerk_id ??
          row.employee_external_id ??
          ""
      ).trim();

      if (!subwayId) return;

      map[subwayId] = {
        employee_id: Number(row.employee_id),
        payweek_name: row.payweek_name || row.employee_name || "",
      };
    });

    return map;
  }, [employeeMappings]);

  const weekData = useMemo(() => {
    const map = {};

    payrollRows.forEach((row) => {
      const employeeName = String(row.employee_name || "").trim() || "Unknown";
      const workDate = formatDate(row.work_date);

      if (!employeeName || !workDate || !currentWeekDates.includes(workDate)) return;

      if (!map[employeeName]) {
        map[employeeName] = {
          employeeName,
          days: {},
          weekHours: 0,
          weekTips: 0,
        };
      }

      if (!map[employeeName].days[workDate]) {
        map[employeeName].days[workDate] = {
          shifts: [],
          dailyHours: 0,
          dailyTips: 0,
        };
      }

      const clockInHour = getDecimalHour(row.clock_in);
      const clockOutHour = getDecimalHour(row.clock_out);
      const hoursWorked = Number(row.hours_worked || 0);

      map[employeeName].days[workDate].shifts.push({
  id: row.id,
  clockIn: `${workDate} ${row.clock_in}`,
  clockOut: `${workDate} ${row.clock_out}`,
  clockInHour,
  clockOutHour,
  hoursWorked,
  is_updated: row.is_updated,
  subway_employee_id: row.subway_employee_id,
});

      map[employeeName].days[workDate].dailyHours += hoursWorked;
      map[employeeName].weekHours += hoursWorked;
    });

    tipRows.forEach((row) => {
      const employeeName = String(row.employee_name || "").trim() || "Unknown";
      const tipDate = formatDate(row.tip_date);

      if (!employeeName || !tipDate || !currentWeekDates.includes(tipDate)) return;

      if (!map[employeeName]) {
        map[employeeName] = {
          employeeName,
          days: {},
          weekHours: 0,
          weekTips: 0,
        };
      }

      if (!map[employeeName].days[tipDate]) {
        map[employeeName].days[tipDate] = {
          shifts: [],
          dailyHours: 0,
          dailyTips: 0,
        };
      }

      const tipAmount = Number(row.employee_split_tip || 0);
      map[employeeName].days[tipDate].dailyTips += tipAmount;
      map[employeeName].weekTips += tipAmount;
    });

    return Object.values(map).sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }, [payrollRows, tipRows, currentWeekDates]);


  useEffect(() => {
  if (scrollRef.current) {
    scrollRef.current.scrollTop = 0;
  }
}, [currentWeekIndex]);

  const finalizeSummary = useMemo(() => {
    const employeeMap = {};

    payrollRows.forEach((row) => {
      const subwayEmployeeId = String(row.subway_employee_id || "").trim();
      const mapping = mappingBySubwayId[subwayEmployeeId];
      if (!mapping?.employee_id) return;

      const workDate = formatDate(row.work_date);
      if (!workDate) return;

      const weekIndex = weeks.findIndex((week) => week.includes(workDate));
      if (weekIndex === -1) return;

      const employeeId = Number(mapping.employee_id);

      if (!employeeMap[employeeId]) {
        employeeMap[employeeId] = {
          employee_id: employeeId,
          employee_name: row.employee_name,
          weeks: {},
          total_tips: 0,
        };
      }

      if (!employeeMap[employeeId].weeks[weekIndex]) {
        employeeMap[employeeId].weeks[weekIndex] = 0;
      }

      employeeMap[employeeId].weeks[weekIndex] += Number(row.hours_worked || 0);
    });

    tipRows.forEach((row) => {
      const subwayEmployeeId = String(row.clerk_id ?? row.subway_employee_id ?? "").trim();
      const mapping = mappingBySubwayId[subwayEmployeeId];
      if (!mapping?.employee_id) return;

      const employeeId = Number(mapping.employee_id);

      if (!employeeMap[employeeId]) {
        employeeMap[employeeId] = {
          employee_id: employeeId,
          employee_name: row.employee_name,
          weeks: {},
          total_tips: 0,
        };
      }

      employeeMap[employeeId].total_tips += Number(row.employee_split_tip || 0);
    });

    return Object.values(employeeMap).map((emp) => {
      let regularHours = 0;
      let overtimeHours = 0;

      Object.values(emp.weeks).forEach((weekHours) => {
        const numericWeekHours = Number(weekHours || 0);
        if (numericWeekHours > 40) {
          regularHours += 40;
          overtimeHours += numericWeekHours - 40;
        } else {
          regularHours += numericWeekHours;
        }
      });

      return {
        employee_id: emp.employee_id,
        employee_name: emp.employee_name,
        regular_hours: Number(regularHours.toFixed(2)),
        overtime_hours: Number(overtimeHours.toFixed(2)),
        total_tips: Number(emp.total_tips.toFixed(2)),
      };
    });
  }, [payrollRows, tipRows, mappingBySubwayId, weeks]);



  const unmappedPayrollCount = useMemo(() => {
    return payrollRows.filter((row) => {
      const subwayEmployeeId = String(row.subway_employee_id || "").trim();
      return !mappingBySubwayId[subwayEmployeeId]?.employee_id;
    }).length;
  }, [payrollRows, mappingBySubwayId]);

  const unmappedTipCount = useMemo(() => {
    return tipRows.filter((row) => {
      const subwayEmployeeId = String(row.clerk_id ?? row.subway_employee_id ?? "").trim();
      return !mappingBySubwayId[subwayEmployeeId]?.employee_id;
    }).length;
  }, [tipRows, mappingBySubwayId]);

  const weekTotalHours = useMemo(
    () => weekData.reduce((sum, employee) => sum + Number(employee.weekHours || 0), 0),
    [weekData]
  );

  const weekTotalTips = useMemo(
    () => weekData.reduce((sum, employee) => sum + Number(employee.weekTips || 0), 0),
    [weekData]
  );

  const getDailyTotalHours = (date) =>
    weekData.reduce((sum, employee) => sum + Number(employee.days[date]?.dailyHours || 0), 0);

  const getDailyTotalTips = (date) =>
    weekData.reduce((sum, employee) => sum + Number(employee.days[date]?.dailyTips || 0), 0);

 const toDatetimeLocalValue = (value, fallbackDate) => {
  const datePart = extractDatePart(value) || fallbackDate;
  const timePart = extractTimePart(value);

  if (!datePart || !timePart) return "";

  return `${datePart}T${timePart.slice(0, 5)}`;
};

const openEditModal = (shift, employeeName, workDate) => {
  setEditingShift({ ...shift, employeeName, workDate });
  setEditClockIn(toDatetimeLocalValue(shift.clockIn, workDate));
  setEditClockOut(toDatetimeLocalValue(shift.clockOut, workDate));
  setShowEditModal(true);
};

  

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingShift(null);
    setEditClockIn("");
    setEditClockOut("");
  };

  const toMysqlDatetime = (value) => `${value}:00`.replace("T", " ");

const addOneDay = (dateStr) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  const next = new Date(year, month - 1, day + 1);
  const y = next.getFullYear();
  const m = String(next.getMonth() + 1).padStart(2, "0");
  const d = String(next.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const handleSaveEdit = async () => {
  if (!editingShift?.id || !editClockIn || !editClockOut) return;

  try {
    setSavingEdit(true);

    let adjustedClockOut = editClockOut;

    if (editClockOut <= editClockIn) {
      const outTime = editClockOut.slice(11, 16);
      adjustedClockOut = `${addOneDay(editingShift.workDate)}T${outTime}`;
    }

    const payload = {
      clock_in: toMysqlDatetime(editClockIn),
      clock_out: toMysqlDatetime(adjustedClockOut),
    };

    const response = await api.put(`/subway-data/payroll-row/${editingShift.id}`, payload);
    const updatedRow = response.data?.row;

    if (updatedRow) {
      setPayrollRows((prev) =>
        prev.map((row) =>
          row.id === updatedRow.id
            ? {
                ...row,
                work_date: updatedRow.work_date,
                clock_in: updatedRow.clock_in,
                clock_out: updatedRow.clock_out,
                hours_worked: updatedRow.hours_worked,
                is_updated: updatedRow.is_updated,
              }
            : row
        )
      );
    }

    closeEditModal();
  } catch (err) {
    console.error("Error updating payroll row:", err.response?.data || err.message);
    alert(err.response?.data?.message || "Failed to update payroll row.");
  } finally {
    setSavingEdit(false);
  }
};

  const handleFinalize = () => {
    if (!finalizeSummary.length) {
      alert("No mapped Subway employees were found to finalize.");
      return;
    }

    if (unmappedPayrollCount > 0 || unmappedTipCount > 0) {
      const proceed = window.confirm(
        `There are unmapped Subway rows.\n\nUnmapped payroll rows: ${unmappedPayrollCount}\nUnmapped tip rows: ${unmappedTipCount}\n\nOnly mapped employees will be sent to payroll. Continue?`
      );
      if (!proceed) return;
    }

    navigate(`/dashboard/business/${businessId}/run-new-pay`, {
      state: {
        subwayFinalize: true,
        subwayPrefillToken: Date.now(),
        subwayPrefill: finalizeSummary,
        subwayStartDate: startDate,
        subwayEndDate: endDate,
        subwayStoreNumber: storeNumber,
      },
    });
  };

  const isLoading = loadingDates || loadingData;

  return (
    <div className="payroll-sync-page">
      <div className="payroll-sync-card">
        <div className="payroll-sync-header">
          <h1 className="pw-page-title">Subway Payroll Sync</h1>

          
        </div>

        <div className="payroll-sync-meta">
          <div>Store Number: {storeNumber || "—"}</div>
          <div>Start Date: {startDate || "—"}</div>
          <div>End Date: {endDate || "—"}</div>
        </div>

        <div className="payroll-sync-meta">
          <div>Mapped Employees Ready: {finalizeSummary.length}</div>
          <div>Unmapped Payroll Rows:{unmappedPayrollCount}</div>
          <div>Unmapped Tip Rows: {unmappedTipCount}</div>
          <div className="buttons-section-sync">
            <button
              type="button"
              className="payroll-sync-finalize-btn"
              onClick={handleFinalize}
              disabled={!finalizeSummary.length}
            >
              Finalize
            </button>

            <button
              type="button"
              className="payroll-sync-back-btn"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
            </div>
          
        </div>

        {mappingWarning ? (
          <div className="payroll-sync-message payroll-sync-message-error">
            {mappingWarning}
          </div>
        ) : null}

        {weeks.length > 1 ? (
          <div className="payroll-sync-week-nav">
            <button
              type="button"
              className="payroll-sync-week-btn"
              onClick={() => setCurrentWeekIndex((prev) => prev - 1)}
              disabled={currentWeekIndex === 0}
            >
              ←
            </button>

            <div className="payroll-sync-week-label">
              Week {currentWeekIndex + 1} of {weeks.length}
            </div>

            <button
              type="button"
              className="payroll-sync-week-btn"
              onClick={() => setCurrentWeekIndex((prev) => prev + 1)}
              disabled={currentWeekIndex >= weeks.length - 1}
            >
              →
            </button>
          </div>
        ) : null}

        {error ? (
          <div className="payroll-sync-message payroll-sync-message-error">
            {error}
          </div>
        ) : null}

        {isLoading ? (
          <div className="payroll-sync-loading">Loading Subway payroll data...</div>
        ) : weekData.length === 0 ? (
          <div className="payroll-sync-empty">No Subway payroll data found for this payroll period.</div>
        ) : (
          <>
          <div className="sync-scroll" ref={scrollRef}>
            {currentWeekDates.map((date) => (
              
              <div key={date} className="payroll-sync-day-block">
                <div className="payroll-sync-day-title">{formatDisplayDate(date)}</div>

                <div className="payroll-sync-time-header">
                  <div className="payroll-sync-name-col" />
                  <div className="payroll-sync-time-label-row">
                    {Array.from({ length: TOTAL_HOURS + 1 }).map((_, index) => {
                      const hour = DAY_START_HOUR + index;
                      const hourLabel =
                        hour === 12 ? "12pm" : hour > 12 ? `${hour - 12}pm` : `${hour}am`;

                      return (
                        <div
                          key={`${date}-hour-${hour}`}
                          className="payroll-sync-time-label"
                          style={{ left: `${((hour - DAY_START_HOUR) / TOTAL_HOURS) * 100}%` }}
                        >
                          {hourLabel}
                        </div>
                      );
                    })}
                  </div>
                  <div className="payroll-sync-hours-col">Daily Hours</div>
                  <div className="payroll-sync-tips-col">Daily Tips</div>
                </div>

                {weekData.map((employee, empIndex) => {
                  const dayInfo = employee.days[date] || {
                    shifts: [],
                    dailyHours: 0,
                    dailyTips: 0,
                  };

                  return (
                    <div key={`${date}-${employee.employeeName}`} className="payroll-sync-employee-row">
                      <div className="payroll-sync-name-col">{employee.employeeName}</div>

                      <div className="payroll-sync-timeline-col payroll-sync-grid">
                        {dayInfo.shifts.map((shift, shiftIndex) => {
  if (shift.clockInHour == null || shift.clockOutHour == null) {
    return null;
  }

  let startHour = shift.clockInHour;
  let endHour = shift.clockOutHour;

  // Overnight shift, but still belongs to same work_date row
  if (endHour <= startHour) {
  startHour = DAY_START_HOUR;
}

  const visibleStart = DAY_START_HOUR; // 6
  const visibleEnd = DAY_END_HOUR;     // 23

  const clippedStart = Math.max(startHour, visibleStart);
  const clippedEnd = Math.min(endHour, visibleEnd);

  if (clippedEnd <= clippedStart) {
    return null;
  }

  const left = ((clippedStart - visibleStart) / TOTAL_HOURS) * 100;
  const width = ((clippedEnd - clippedStart) / TOTAL_HOURS) * 100;

  return (
    <div
      key={shift.id || `${employee.employeeName}-${date}-${shiftIndex}`}
      className={`payroll-sync-bar payroll-sync-bar-${(empIndex + shiftIndex) % 4}${shift.is_updated ? " payroll-sync-bar-updated" : ""}`}
      style={{
        left: `${left}%`,
        width: `${width}%`,
      }}
      title={`${formatTimeLabel(shift.clockIn)} - ${formatTimeLabel(shift.clockOut)} | ${Number(shift.hoursWorked || 0).toFixed(2)} hrs`}
      onClick={() => openEditModal(shift, employee.employeeName, date)}
    >
      <span className="payroll-sync-bar-tooltip">
        {formatTimeLabel(shift.clockIn)} - {formatTimeLabel(shift.clockOut)}
      </span>
    </div>
  );
})}
                      </div>

                      <div className="payroll-sync-hours-col">
                        {Number(dayInfo.dailyHours || 0).toFixed(2)}
                      </div>

                      <div className="payroll-sync-tips-col">
                        {Number(dayInfo.dailyTips || 0).toFixed(2)}
                      </div>
                    </div>
                  );
                })}

                <div className="payroll-sync-employee-row payroll-sync-daily-total">
                  <div className="payroll-sync-name-col">Total</div>
                  <div />
                  <div className="payroll-sync-hours-col">{getDailyTotalHours(date).toFixed(2)}</div>
                  <div className="payroll-sync-tips-col">{getDailyTotalTips(date).toFixed(2)}</div>
                </div>
              </div>
            ))}
            </div>

            <div className="payroll-sync-week-summary">
              <div className="payroll-sync-summary-title">Weekly Totals</div>

              <div className="payroll-sync-summary-table">
                {weekData.map((employee) => (
                  <div key={`summary-${employee.employeeName}`} className="payroll-sync-summary-row">
                    <div className="payroll-sync-summary-name">{employee.employeeName}</div>
                    <div className="payroll-sync-summary-value">
                      Hours: {Number(employee.weekHours || 0).toFixed(2)}
                    </div>
                    <div className="payroll-sync-summary-value">
                      Tips: {Number(employee.weekTips || 0).toFixed(2)}
                    </div>
                  </div>
                ))}

                <div className="payroll-sync-summary-row payroll-sync-summary-grand">
                  <div className="payroll-sync-summary-name">Week Total</div>
                  <div className="payroll-sync-summary-value">Hours: {weekTotalHours.toFixed(2)}</div>
                  <div className="payroll-sync-summary-value">Tips: {weekTotalTips.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showEditModal && editingShift ? (
        <div className="payroll-sync-modal-overlay">
          <div className="payroll-sync-modal">
            <h2 className="payroll-sync-modal-title">Edit Subway Time</h2>

            <div className="payroll-sync-modal-row">
              <strong>Employee:</strong> {editingShift.employeeName}
            </div>

            <div className="payroll-sync-modal-row">
              <strong>Date:</strong> {formatDisplayDate(editingShift.workDate)}
            </div>

            <div className="payroll-sync-modal-field">
              <label>Clock In</label>
              <input
                type="datetime-local"
                value={editClockIn}
                onChange={(e) => setEditClockIn(e.target.value)}
              />
            </div>

            <div className="payroll-sync-modal-field">
              <label>Clock Out</label>
              <input
                type="datetime-local"
                value={editClockOut}
                onChange={(e) => setEditClockOut(e.target.value)}
              />
            </div>

            <div className="payroll-sync-modal-actions">
              <button
                type="button"
                className="payroll-sync-modal-cancel"
                onClick={closeEditModal}
                disabled={savingEdit}
              >
                Cancel
              </button>

              <button
                type="button"
                className="payroll-sync-modal-save"
                onClick={handleSaveEdit}
                disabled={savingEdit}
              >
                {savingEdit ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PayrollSyncPage;

